/**
 * T1-013 — offline-guard primitives for mutations.
 *
 * `isOnline()` reads `navigator.onLine`. Returns true in non-browser
 * contexts (defensive — server-side rendering or test environments
 * default to "assume online" so callers don't accidentally fail).
 *
 * `OfflineError` is the typed error mutations should throw when
 * `isOnline() === false`. React Query / TanStack Query catches it the
 * same as any other error; the UI layer (OfflineBanner + per-mutation
 * onError) decides how to surface.
 */

export class OfflineError extends Error {
  readonly kind = 'OfflineError' as const;
  constructor(operation: string) {
    super(`Cannot perform "${operation}" while offline.`);
    this.name = 'OfflineError';
  }
}

export function isOnline(): boolean {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine;
}

/**
 * Wrap a mutation function so it short-circuits to OfflineError when
 * offline. Usage:
 *
 *   const updateProfile = guardOffline('update profile', async (input) => {
 *     return apiFetch('/api/v1/me/profile', { method: 'PATCH', ... });
 *   });
 */
export function guardOffline<TArgs extends unknown[], TResult>(
  operation: string,
  fn: (...args: TArgs) => Promise<TResult>,
): (...args: TArgs) => Promise<TResult> {
  return async (...args: TArgs): Promise<TResult> => {
    if (!isOnline()) {
      throw new OfflineError(operation);
    }
    return fn(...args);
  };
}
